import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import useForm from 'hooks/useForm'
import { parentChainNames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import hotelApi from 'services/api/hotel'
import subsidiariesApi from 'services/api/subsidiaries'
import userApi from 'services/api/user'
import { useChainOptions } from 'utils/manageHotel/chainsHelper'

import {
  getSubsidiaryList,
  postAndSelectSubsidiary,
  savePrevPath
} from '../../actions/index'
import { saveSubsidiary } from '../../utils/helpers'
import ExtraServices from './Components/ExtraServices'
import HotelInfoSection from './Components/HotelInfoSection'
import ImportantUsersSection from './Components/ImportantUsersSection'
import NewHotelHeader from './Components/NewHotelHeader'
import PackageSelection from './Components/PackageSelection'
import { prepareFormDataForSubmission, validateAddHotelForm } from './helpers'
import styles from './NewHotel.module.scss'

const NewHotelDialog = ({
  onClose,
  onAdded,
  hotelName,
  city,
  stateCode,
  countryCode,
  postalCode,
  streetAddress,
  placesId,
  merchantId
}) => {
  const [formData, handleFormChange, setFormData] = useForm({
    merchantId,
    hotelName,
    pms: '',
    serviceStartDate: moment().add(1, 'days').format('YYYY-MM-DD'),
    salesPersonUserId: null,
    csmId: null,
    referrerId: null,
    newReferrerName: null,
    supportEmail: '',
    supportPhone: '',
    numberOfRooms: '',
    parentChainCode: '',
    chainCode: '',
    streetAddress,
    city,
    stateCode,
    countryCode,
    postalCode,
    placesId,
    invoiceAddress:
      `${streetAddress}, ${city}, ${stateCode} ${postalCode}, ${countryCode}`.trim(),
    isAutoPayEnabled: false,
    paymentPeriod: 'semiAnnual',
    discount: '',
    subscriptions: [
      {
        plan: 'directfulCore',
        fee: 0
      }
    ],
    newUsers: [
      {
        fullName: '',
        email: '',
        phone: '',
        timeZoneName: '',
        isGeneralManager: true,
        isAccountBillable: true
      }
    ],
    hasSetupFee: true,
    setupFee: '',
    hasPremiumSupport: false,
    premiumSupportFee: '',
    selectedPackage: 'directfulCore',
    monthlyRate: ''
  })

  const { addToast } = useToasts()
  const [chains, setParent] = useChainOptions()
  const dispatch = useDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [salesAgents, setSalesAgents] = useState([])
  const [chainName, setChainName] = useState()
  const [referrers, setReferrers] = useState()
  const [csmUsers, setCsmUsers] = useState([])
  const [brand, setBrand] = useState('')

  useEffect(() => {
    Promise.all([
      userApi.getSalesAgents(),
      userApi.getCustomerSuccessManagers(),
      subsidiariesApi.referrers.get()
    ]).then(([salesAgentsRes, csmUsersRes, referrersRes]) => {
      setSalesAgents(salesAgentsRes)
      setCsmUsers(csmUsersRes)
      setReferrers(referrersRes.map(r => ({ value: r.id, label: r.name })))
    })
  }, [])

  useEffect(() => {
    if (formData.parentChainCode === 'independent' && formData.chainCode !== 'i') {
      setFormData({ chainCode: 'i' })
    }
  }, [formData.chainCode, formData.parentChainCode, setFormData])

  useEffect(() => {
    setFormData({ chainCode: brand || chainName })
  }, [chainName, brand, setFormData])

  useEffect(() => {
    const formattedAddress =
      `${formData.streetAddress}, ${formData.city}, ${formData.stateCode} ${formData.postalCode}, ${formData.countryCode}`.trim()
    if (formattedAddress !== ', , ,') {
      setFormData({ invoiceAddress: formattedAddress })
    }
  }, [
    formData.streetAddress,
    formData.city,
    formData.stateCode,
    formData.postalCode,
    formData.countryCode,
    setFormData
  ])

  function handleParentChange(e) {
    setParent(e.target.value)
    handleFormChange(e)
  }

  function addNewHotel() {
    if (!validateAddHotelForm(formData, addToast)) {
      return
    }

    setIsSubmitting(true)
    const formDataToSubmit = prepareFormDataForSubmission(formData)

    hotelApi
      .addHotel(formDataToSubmit)
      .then(res => {
        if (res) {
          addToast('Hotel added successfully', { appearance: 'success' })
          dispatch(getSubsidiaryList())

          // Switch to the newly created hotel
          if (res.id) {
            dispatch(savePrevPath(window.location.pathname))
            saveSubsidiary({ subsidiaryId: res.id })
            dispatch(postAndSelectSubsidiary(res.id))
          }

          onAdded(res)
          onClose()
        }
      })
      .catch(err => {
        addToast(`${err.toString()}`, { appearance: 'error' })
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  return (
    <ConfirmationDialog
      title="Add new hotel"
      maxWidth={800}
      hasHeader={false}
      theme="blacklist"
      contentPadding="0px"
      positiveMessage="Save hotel"
      negativeMessage="Cancel"
      onConfirm={addNewHotel}
      onClose={onClose}
      isButtonDisabled={isSubmitting}
    >
      {isSubmitting && (
        <div className={styles.loadingContainer}>
          <LoadingIndicator longWaitingTreshold={30} />
        </div>
      )}
      <NewHotelHeader
        formData={formData}
        brand={brand}
        setBrand={setBrand}
        chainName={chainName}
      />
      <div className={styles.wrapper}>
        <HotelInfoSection
          formData={formData}
          handleFormChange={handleFormChange}
          setFormData={setFormData}
          salesAgents={salesAgents}
          csmUsers={csmUsers}
          referrers={referrers}
          parentChainNames={parentChainNames}
          chains={chains}
          chainName={chainName}
          handleParentChange={handleParentChange}
          setChainName={setChainName}
        />
        <hr className={styles.divider} />
        <PackageSelection
          formData={formData}
          setFormData={setFormData}
          handleFormChange={handleFormChange}
        />
        <hr className={styles.divider} />
        <ExtraServices
          formData={formData}
          handleFormChange={handleFormChange}
          setFormData={setFormData}
        />
        <hr className={styles.divider} />
        <ImportantUsersSection
          formData={formData}
          handleFormChange={handleFormChange}
          setFormData={setFormData}
        />
      </div>
    </ConfirmationDialog>
  )
}

export default NewHotelDialog
